import { useState } from "react";
import { SearchSectionForm } from "src/components/common/form/search/SearchSectionForm";
import { SearchMonthSingleForm } from "src/components/common/form/search/SearchMonthSingleForm";
import { SearchDropDownForm } from "src/components/common/form/search/SearchDropDownForm";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { formatLocalYearMonth } from "src/utils/date/dateUtils";
import { useHRM2040_R01Store } from "./Store_HRM2040_R01";

/** 상위 화면에서 전달하는 월근태 재집계 동작이다. */
interface Props {
    onAggregateClick: () => void; // 직전 조회조건으로 재집계를 요청한다.
}

/**
 * 근무년월·사업장·부서 검색 조건과 재집계 버튼을 표시한다.
 * @param onAggregateClick 재집계 버튼 클릭 함수
 * @returns 현재 조회조건이 반영된 검색 영역
 */
export const HRM2040_R01SearchSection = ({ onAggregateClick }: Props) => {
    // 화면 Store에서 검색 조건과 변경 함수를 가져온다.
    const { HRM2040_R01Filter, setHRM2040_R01Filter } = useHRM2040_R01Store();
    // 구조 분해: 각 검색 항목의 현재 값을 꺼낸다.
    const { workYm, compCd, orgCd } = HRM2040_R01Filter;
    // 공통 Store에서 사업장·부서 선택 목록을 가져온다.
    const { compCdList, orgCdList } = useCommonCodeStore();

    // true이면 월 선택기를 표시한다.
    const [isMonthPickerVisible, setIsMonthPickerVisible] = useState(false);
    // 월 선택기를 입력 칸 아래에 띄울 위치이다.
    const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });

    // YYYYMM의 연도와 월로 현지 날짜를 만들어 선택기에 표시한다.
    const selectedMonth = workYm.length === 6
        ? new Date(Number(workYm.slice(0, 4)), Number(workYm.slice(4, 6)) - 1, 1)
        : undefined;

    /**
     * 선택한 월을 YYYYMM으로 바꿔 검색 조건에 저장한다.
     * @param date 월 선택기에서 선택한 날짜
     * @returns 반환값은 없다.
     */
    const handleMonthChange = (date: Date | undefined): void => {
        if (!date) return;
        setHRM2040_R01Filter({ workYm: formatLocalYearMonth(date) });
    };

    return (
        <SearchSectionForm>
            {/* 근무년월을 Date로 표시하고 선택한 값을 YYYYMM으로 저장한다. */}
            <SearchMonthSingleForm
                label="근무년월"
                visible={isMonthPickerVisible}
                setVisible={setIsMonthPickerVisible}
                value={selectedMonth}
                onChange={handleMonthChange}
                pickerPosition={pickerPosition}
                setPickerPosition={setPickerPosition}
            />
            {/* 사업장 선택에는 공통 사업장 목록을 사용한다. */}
            <SearchDropDownForm
                label="사업장"
                value={compCd}
                optionList={compCdList}
                onChangeItem={item => setHRM2040_R01Filter({ compCd: item?.compCd || '' })}
            />
            {/* 부서 선택에는 공통 부서 목록을 사용한다. */}
            <SearchDropDownForm
                label="부서"
                value={orgCd}
                optionList={orgCdList}
                onChangeItem={item => setHRM2040_R01Filter({ orgCd: item?.orgCd || '' })}
            />
            {/* 직전 조회조건으로 월근태 재집계 확인창을 연다. */}
            <button
                type="button"
                onClick={onAggregateClick}
                className="flex-shrink-0 bg-white hover:bg-gray-100 ml-20 border border-gray-300 text-gray-800 font-semibold px-4 rounded shadow h-[30px]"
            >
                월근태 재집계
            </button>
        </SearchSectionForm>
    );
};
